import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

function formatPercent(value) {
	return `${Number(value || 0).toFixed(1).replace(".0", "")}%`
}

function formatGrowth(value, unit) {
	if (value === null || value === undefined) return unit === "pp" ? "—" : "новое"
	const sign = Number(value) >= 0 ? "+" : ""
	const number = Number(value).toFixed(1).replace(".0", "")
	return unit === "pp" ? `${sign}${number} п.п.` : `${sign}${number}%`
}

function formatMetric(value) {
	const number = Number(value || 0)
	return formatMoney(number, 0)
}

function metricActual(metric) {
	return metric && typeof metric === "object" ? metric.actual : metric
}

function metricGrowth(metric) {
	return metric && typeof metric === "object" ? metric.growth_percent : null
}

function shortMetric(shortValue, value) {
	// Полное число из value; готовая строка с бэка — только если числа нет
	if (value !== null && value !== undefined) return formatMetric(value)
	return shortValue || formatMetric(0)
}

function growthClass(value, inverse = false) {
	if (value === null || value === undefined) return "text-muted"
	const positive = Number(value) >= 0
	return (inverse ? !positive : positive) ? "text-success" : "text-danger"
}

function planColor(percent) {
	const value = Number(percent || 0)
	if (value >= 100) return "#20bf87"
	if (value >= 80) return "#f0ad35"
	return "#e35d5d"
}

function SectionCard({ title, subtitle, children, titleIcon = null, actions = null }) {
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start gap-3 mb-3">
					<div>
						<h5 className="mb-1 d-flex align-items-center gap-2">
							{titleIcon}
							{title}
						</h5>
						{subtitle && <div style={mutedStyle}>{subtitle}</div>}
					</div>
					{actions}
				</div>
				{children}
			</div>
		</div>
	)
}

function KpiCard({ title, value, metric, icon, compareLabel, progress = null }) {
	const growth = metricGrowth(metric)
	const unit = metric?.growth_unit
	const hasGrowth = metric !== undefined && metric !== null
	return (
		<div className="card mb-3 h-100" style={cardStyle}>
			<div className="card-body py-3 px-4">
				<div className="d-flex align-items-start gap-2 h-100">
					<i className={`uil ${icon} text-muted`} style={{ fontSize: "16px", lineHeight: "20px" }}></i>
					<div className="d-flex flex-column justify-content-between w-100">
						<div style={mutedStyle}>{title}</div>
						<h2 className="mb-1" style={{ fontSize: "34px", lineHeight: "40px", fontWeight: 800, letterSpacing: "-0.5px", color: "var(--dark-text-strong, #111827)" }}>
							{value}
						</h2>
						<div className="d-flex align-items-center gap-2" style={{ fontSize: "12px", minHeight: "22px" }}>
							{hasGrowth && (
								<span
									className={growthClass(growth)}
									style={{
										background: growth === null || growth === undefined ? "var(--dark-surface-2, #f1f4f8)" : Number(growth) >= 0 ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-tint-danger, #fdecec)",
										borderRadius: "14px",
										padding: "2px 8px",
										whiteSpace: "nowrap",
									}}
								>
									{formatGrowth(growth, unit)}
								</span>
							)}
							{compareLabel && <span className="text-muted">{compareLabel}</span>}
						</div>
						{progress !== null && (
							<div className="progress mt-2" style={{ height: "7px", borderRadius: "20px" }}>
								<div className="progress-bar" style={{ width: `${Math.min(Number(progress) || 0, 100)}%`, background: planColor(progress) }}></div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

function HealthRow({ title, value, score, total = 20 }) {
	const color = planColor(Number(score || 0) / Math.max(total, 1) * 100)
	return (
		<div className="d-flex justify-content-between align-items-center py-2 px-3 rounded-3" style={{ background: "var(--dark-surface-2, #f8fafc)" }}>
			<div>{title}</div>
			<div className="d-flex align-items-center gap-3">
				<span className="text-muted small">{value}</span>
				<b style={{ color }}>{score}/{total}</b>
			</div>
		</div>
	)
}

function HealthBlock({ data }) {
	const summary = data?.summary || {}
	const planPercent = Number(data?.plan?.execution_percent || 0)
	const hasPlan = Number(data?.plan?.value || 0) > 0
	// Доля доставки — та же формула, что в блоке «Доставка»: чеки доставки / все чеки
	const deliveryShare = Number(data?.delivery_summary?.delivery_share_percent || 0)
	const margin = Number(metricActual(summary.margin_percent) || 0)
	const averageChequeGrowth = Number(metricGrowth(summary.average_cheque) || 0)
	const revenueGrowth = Number(metricGrowth(summary.revenue) || 0)

	const rows = [
		{ title: "План", value: hasPlan ? formatPercent(planPercent) : "нет плана", score: hasPlan ? Math.round(Math.min(planPercent, 100) / 100 * 25) : 0, total: 25 },
		{ title: "Доставка", value: formatPercent(deliveryShare), score: Math.round(Math.min(deliveryShare, 40) / 40 * 20), total: 20 },
		{ title: "Маржа", value: formatPercent(margin), score: Math.round(Math.min(Math.max(margin, 0), 50) / 50 * 15), total: 15 },
		{ title: "Выручка к прошлому периоду", value: formatGrowth(revenueGrowth), score: revenueGrowth >= 0 ? 20 : Math.max(0, 20 + Math.round(revenueGrowth)), total: 20 },
		{ title: "Средний чек к прошлому периоду", value: formatGrowth(averageChequeGrowth), score: averageChequeGrowth >= 0 ? 20 : Math.max(0, 20 + Math.round(averageChequeGrowth)), total: 20 },
	]
	const totalScore = rows.reduce((sum, item) => sum + item.score, 0)
	const maxScore = rows.reduce((sum, item) => sum + item.total, 0)
	const healthScore = Math.round(totalScore / Math.max(maxScore, 1) * 100)

	return (
		<SectionCard title="Здоровье филиала" titleIcon={<i className="uil uil-heart-rate"></i>}>
			<div className="row align-items-center g-4">
				<div className="col-md-2">
					<div
						className="rounded-circle d-flex align-items-center justify-content-center mx-auto fw-bold"
						style={{ width: "96px", height: "96px", border: `10px solid ${planColor(healthScore)}`, color: planColor(healthScore), fontSize: "28px" }}
					>
						{healthScore}
					</div>
				</div>
				<div className="col-md-10">
					<div className="row g-2">
						{rows.map(item => (
							<div className="col-md-6" key={item.title}>
								<HealthRow {...item} />
							</div>
						))}
					</div>
				</div>
			</div>
		</SectionCard>
	)
}

function CompareCard({ title, value, networkValue, growth, unit }) {
	return (
		<div className="p-3 rounded-3 h-100" style={{ background: "var(--dark-surface-2, #f8fafc)" }}>
			<div style={mutedStyle}>{title}</div>
			<h4 className="mb-1">{value}</h4>
			<div className="text-muted small">Сеть: {networkValue}</div>
			<div className={`small mt-1 ${growthClass(growth)}`}>
				{formatGrowth(growth, unit)} к сети
			</div>
		</div>
	)
}

function NetworkComparison({ data }) {
	const network = data?.network_comparison || {}

	const cards = [
		{
			title: "Выручка/день",
			value: shortMetric(network.revenue_per_day?.value_short, network.revenue_per_day?.value),
			networkValue: shortMetric(network.revenue_per_day?.network_value_short, network.revenue_per_day?.network_value),
			growth: network.revenue_per_day?.diff_percent,
		},
		{
			title: "Средний чек",
			value: shortMetric(network.average_cheque?.value_short, network.average_cheque?.value),
			networkValue: shortMetric(network.average_cheque?.network_value_short, network.average_cheque?.network_value),
			growth: network.average_cheque?.diff_percent,
		},
		{
			title: "Чеков/день",
			value: formatMoney(network.cheques_per_day?.value, 1),
			networkValue: formatMoney(network.cheques_per_day?.network_value, 1),
			growth: network.cheques_per_day?.diff_percent,
		},
		{
			title: "Доля доставки",
			value: formatPercent(network.delivery_share?.value),
			networkValue: formatPercent(network.delivery_share?.network_value),
			growth: network.delivery_share?.diff_percent,
			unit: "pp",
		},
	]

	return (
		<SectionCard
			title="Сравнение с сетью"
			subtitle={network.branches_with_sales ? `Сеть — среднее на филиал по ${network.branches_with_sales} филиалам с продажами в периоде` : null}
			titleIcon={<i className="uil uil-chart-bar"></i>}
		>
			<div className="row g-3">
				{cards.map(item => (
					<div className="col-xl-3 col-md-6" key={item.title}>
						<CompareCard {...item} />
					</div>
				))}
			</div>
		</SectionCard>
	)
}

function BestWorstDay({ data }) {
	const weekdayData = data?.weekday_performance || {}
	const items = weekdayData.data || []
	const best = weekdayData.best
	const worst = weekdayData.worst
	const maxRevenue = Math.max(...items.map(item => Number(item.revenue || 0)), 1)

	return (
		<SectionCard title="Лучший и худший день" subtitle="По средней выручке одного такого дня недели в периоде" titleIcon={<i className="uil uil-calendar-alt"></i>}>
			<div className="d-flex justify-content-around align-items-end gap-3 mb-3" style={{ minHeight: "86px" }}>
				{items.map(item => {
					const isBest = best && item.weekday === best.weekday
					const isWorst = worst && item.weekday === worst.weekday
					const height = item.days_count > 0 ? Math.max(Number(item.revenue || 0) / maxRevenue * 74, 18) : 4
					const color = isWorst ? "#d8343f" : isBest ? "#20bf87" : "#edf0f4"
					return (
						<div className="text-center" key={item.weekday} title={item.days_count > 0 ? `${shortMetric(item.revenue_short, item.revenue)} в среднем · дней: ${item.days_count}` : "нет таких дней в периоде"}>
							<div className="mx-auto mb-2" style={{ width: "32px", height: `${height}px`, background: color, borderRadius: "10px 10px 2px 2px" }}></div>
							<div className={isWorst ? "text-danger fw-semibold" : isBest ? "text-success fw-semibold" : "text-muted"} style={{ fontSize: "12px" }}>
								{item.short_name}
							</div>
						</div>
					)
				})}
			</div>
			<div className="d-flex flex-wrap align-items-center gap-3 small">
				{best && (
					<div>
						<span className="text-success">☼</span> Лучший день: <b>{best.name}</b>
						<span className="text-success ms-1">{formatGrowth(best.diff_percent)} к среднему</span>
					</div>
				)}
				{worst && (
					<div>
						<span className="text-danger">〰</span> Худший день: <b>{worst.name}</b>
						<span className="text-danger ms-1">{formatGrowth(worst.diff_percent)} к среднему</span>
					</div>
				)}
				{!best && !worst && <span className="text-muted">Выберите период хотя бы из двух разных дней недели</span>}
			</div>
		</SectionCard>
	)
}

function PeakHours({ trend }) {
	const categories = trend?.categories || []
	const currentSeries = (trend?.series || []).find(item => item.key === "current") || { data: [] }
	const values = currentSeries.data || []
	const maxValue = Math.max(...values.map(value => Number(value || 0)), 1)
	const peakThreshold = maxValue * 0.85
	const peaks = values
		.map((value, index) => ({ value: Number(value || 0), index }))
		.filter(item => item.value >= peakThreshold && item.value > 0)
	const peakLabel = peaks.length > 0
		? peaks.map(item => categories[item.index]).join(", ")
		: "Нет данных"

	return (
		<SectionCard title="Пиковые часы" subtitle="Выручка по часу закрытия чека, ось — от начала бизнес-дня" titleIcon={<i className="uil uil-fire"></i>}>
			<div className="d-flex align-items-center gap-1 mb-1">
				{values.map((value, index) => {
					const intensity = Number(value || 0) / maxValue
					const color = intensity > 0.85 ? "#d8343f" : intensity > 0.65 ? "#dc5962" : intensity > 0.35 ? "#e69ca1" : "#f3c7c9"
					return (
						<div
							className="flex-grow-1"
							key={categories[index] || index}
							title={`${categories[index]}: ${formatMoney(value, 0)}`}
							style={{ height: "38px", background: color, borderRadius: "7px" }}
						></div>
					)
				})}
			</div>
			<div className="d-flex align-items-center gap-1 mb-2">
				{values.map((_, index) => (
					<div className="flex-grow-1 text-muted text-center" style={{ fontSize: "10px", minHeight: "14px" }} key={categories[index] || index}>
						{index % 3 === 0 ? String(categories[index] || "").slice(0, 2) : ""}
					</div>
				))}
			</div>
			<div className="small">Пик: {peakLabel}</div>
		</SectionCard>
	)
}

function PlanHistory({ data }) {
	const items = data?.data || []
	const chart = {
		series: [{ name: "Выполнение", data: items.map(item => Number(item.execution_percent || 0)) }],
		options: {
			chart: { toolbar: { show: false }, zoom: { enabled: false } },
			plotOptions: { bar: { borderRadius: 4, columnWidth: "18%", distributed: true } },
			colors: items.map(item => {
				if (item.is_current) return "#f9dd76"
				if (Number(item.execution_percent || 0) >= 100) return "#20bf87"
				if (Number(item.execution_percent || 0) >= 80) return "#f4c400"
				return "#f44343"
			}),
			dataLabels: {
				enabled: true,
				formatter: value => formatPercent(value),
				offsetY: -18,
				style: { colors: ["#1f2937"], fontSize: "12px", fontWeight: 700 },
			},
			xaxis: {
				categories: items.map(item => item.is_current ? [item.label, "текущий"] : item.label),
				labels: { style: { fontSize: "12px" } },
			},
			yaxis: { min: 0, max: 130, tickAmount: 3, labels: { formatter: value => formatPercent(value) } },
			grid: { borderColor: "#e9edf2", strokeDashArray: 4 },
			legend: { show: false },
			tooltip: {
				y: {
					formatter: (value, { dataPointIndex }) => {
						const item = items[dataPointIndex]
						return item ? `${formatPercent(value)} · ${item.revenue_short} из ${item.plan_short}` : formatPercent(value)
					},
				},
			},
		},
	}

	return (
		<SectionCard title="История планов (6 мес)" subtitle="Ручной план месяца, а где его нет — авто-план по 4 неделям до начала месяца" titleIcon={<i className="uil uil-chart"></i>}>
			<ReactApexChart options={chart.options} series={chart.series} type="bar" height={190} />
		</SectionCard>
	)
}

function WeeklyTrend({ data }) {
	const items = data?.data || []
	return (
		<SectionCard title="Тренд последних 4 недель" titleIcon={<span className="text-success">↗</span>}>
			<div className="row g-3">
				{items.map((item, index) => (
					<div className="col-xl-3 col-md-6" key={item.week_start || index}>
						<div
							className="p-3 rounded-3 h-100 text-center"
							style={{
								background: "var(--dark-surface-2, #f8fafc)",
								border: item.is_current ? "1px solid var(--bs-primary)" : "1px solid var(--dark-border, #f1f4f8)",
							}}
						>
							<div className="text-muted small mb-2">
								{item.label}{item.is_partial ? ` (неполная, ${item.days} дн.)` : ""}
							</div>
							<h4 className="mb-1">{shortMetric(item.revenue_short, item.revenue)}</h4>
							{index > 0 && (
								<div className={`small ${growthClass(item.growth_percent)}`}>
									{formatGrowth(item.growth_percent)}{item.is_partial ? " к тем же дням" : ""}
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		</SectionCard>
	)
}

function RevenueDynamics({ data }) {
	const chart = {
		series: data?.series || [],
		options: {
			chart: { toolbar: { show: false }, zoom: { enabled: false } },
			colors: ["#c44b55"],
			dataLabels: { enabled: false },
			stroke: { curve: "smooth", width: 2 },
			fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.22, opacityTo: 0.02, stops: [0, 100] } },
			xaxis: { categories: data?.categories || [], labels: { rotate: 0, style: { fontSize: "11px" } }, tickAmount: 16 },
			yaxis: { labels: { formatter: value => formatMetric(value) } },
			grid: { borderColor: "#e9edf2", strokeDashArray: 4 },
			legend: { show: false },
			tooltip: { y: { formatter: value => formatMoney(value, 0) } },
		},
	}

	return (
		<SectionCard title="Динамика выручки (90 дн)" subtitle="Выручка за вычетом возвратов по дням до конца выбранного периода">
			<ReactApexChart options={chart.options} series={chart.series} type="area" height={300} />
		</SectionCard>
	)
}

// Сервер уже отдаёт настоящие растущие / падающие позиции (абсолютное изменение выручки
// ко прошлому периоду по всем товарам) — здесь только показываем, без пересортировки
function ProductMovementList({ title, items = [], type = "growth" }) {
	const isGrowth = type === "growth"
	const maxAmount = Math.max(...items.map(item => Math.abs(Number(item.growth_amount || 0))), 1)
	const color = isGrowth ? "#9edfc2" : "#e4a4ad"
	const textColor = isGrowth ? "var(--dark-fg-success, #1f9d67)" : "#c43d4a"

	return (
		<SectionCard title={title} subtitle="Изменение выручки к предыдущему периоду" titleIcon={<i className={`uil ${isGrowth ? "uil-arrow-growth" : "uil-chart-down"}`}></i>}>
			{items.map((item, index) => {
				const width = Math.max(Math.abs(Number(item.growth_amount || 0)) / maxAmount * 100, 8)
				return (
					<div className="mb-3" key={item.product_id || index}>
						<div className="d-flex justify-content-between align-items-center gap-3 mb-1">
							<div className="text-truncate">
								{index + 1}. {item.product_name}
							</div>
							<div className="d-flex align-items-center gap-2 flex-shrink-0">
								<b style={{ color: textColor }}>
									{Number(item.growth_amount || 0) > 0 ? "+" : ""}{shortMetric(item.growth_amount_short, item.growth_amount)}
								</b>
								<span style={{ color: textColor }}>{formatGrowth(item.revenue_growth_percent)}</span>
								<span className="text-muted small">
									({formatMoney(item.quantity_compare || 0)} → {formatMoney(item.quantity || 0)} {item.unit_name || "шт"})
								</span>
							</div>
						</div>
						<div style={{ height: "7px", background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "10px" }}>
							<div style={{ width: `${width}%`, height: "7px", background: color, borderRadius: "10px" }}></div>
						</div>
					</div>
				)
			})}
			{items.length === 0 && <div className="text-muted">Нет данных</div>}
		</SectionCard>
	)
}

function DeliveryBlock({ data }) {
	const items = [
		{
			icon: "uil-compass",
			label: "Доля доставки",
			hint: "чеки доставки / все чеки",
			value: formatPercent(data?.delivery_share_percent),
			growth: formatGrowth(data?.delivery_share_growth, "pp"),
			growthValue: data?.delivery_share_growth,
			color: "var(--dark-fg-primary, #2f80c9)",
			background: "var(--dark-tint-primary, #eef5ff)",
		},
		{
			icon: "uil-stopwatch",
			label: "Время своей доставки",
			hint: "медиана, от открытия до закрытия чека, 0–180 мин",
			value: data?.median_delivery_minutes !== null && data?.median_delivery_minutes !== undefined
				? `${formatMoney(data.median_delivery_minutes, 0)} мин`
				: "—",
			growth: data?.median_delivery_minutes_compare ? `было ${data.median_delivery_minutes_compare} мин` : null,
			color: "#c27a2c",
			background: "var(--dark-tint-warning, #fff4e8)",
		},
		{
			icon: "uil-cube",
			label: "Заказы доставки",
			hint: "все каналы доставки",
			value: shortMetric(data?.delivery_orders_short, data?.delivery_orders),
			growth: formatGrowth(data?.delivery_orders_growth_percent),
			growthValue: data?.delivery_orders_growth_percent,
			color: "var(--dark-fg-success, #1f9d67)",
			background: "var(--dark-tint-success, #eafaf1)",
		},
	]

	return (
		<SectionCard title="Доставка" titleIcon={<i className="uil uil-truck"></i>}>
			<div className="row g-0">
				{items.map((item, index) => (
					<div className="col-md-4 text-center py-4" style={{ borderLeft: index === 0 ? "none" : "1px solid var(--dark-border, #e5eaf1)" }} key={item.label}>
						<div className="rounded-3 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: "34px", height: "34px", color: item.color, background: item.background }}>
							<i className={`uil ${item.icon}`}></i>
						</div>
						<div style={mutedStyle}>{item.label}</div>
						<h2 className="mb-0" style={{ fontWeight: 800, color: "var(--dark-text-strong, #111827)" }}>{item.value}</h2>
						{item.growth && <div className={`small ${item.growthValue !== undefined ? growthClass(item.growthValue) : "text-muted"}`}>{item.growth}</div>}
						<div className="text-muted" style={{ fontSize: "11px" }}>{item.hint}</div>
					</div>
				))}
			</div>
		</SectionCard>
	)
}

function Unit() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("yesterday"),
		pos_id: reduxAccount.pos_id ?? -1,
	}, searchWithFilter)
	const { draft, applied, patch, apply } = filters
	const options = useAnalyticsOptions([], draft.pos_id)

	async function searchWithFilter(filter) {
		// Отчёт только по одному филиалу: «все филиалы» из общего фильтра сюда не подходят
		if (!filter.pos_id || Number(filter.pos_id) <= 0) return
		const response = await GET(buildAnalyticsUrl("/analytics/unit", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// Пришли со «всеми филиалами» с другого экрана — берём первый филиал
	useEffect(() => {
		if (options.poses.length > 0 && (!applied.pos_id || Number(applied.pos_id) <= 0)) {
			apply({ ...applied, pos_id: options.poses[0].id })
		}
	}, [options.poses]) // eslint-disable-line react-hooks/exhaustive-deps

	const summary = data?.summary || {}
	const compareLabel = data?.compare_label

	return (
		<div className="pb-3">
			<AnalyticsFilterBar
				title="Unit"
				icon="uil-store"
				subtitle={data?.unit?.address || data?.unit?.name}
				filters={filters}
				presets={["yesterday", "7days", "30days", "this_month", "last_month", "custom"]}
			>
				<FilterField label="Филиал">
					<FilterSelect options={options.poses} value={draft.pos_id} placeholder="Выберите филиал" onChange={(id) => patch({ pos_id: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row align-items-stretch mb-3">
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard title="Выручка" value={formatMetric(metricActual(summary.revenue))} metric={summary.revenue} compareLabel={compareLabel} icon="uil-money-bill" />
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard title="Чеки" value={formatMoney(metricActual(summary.cheques))} metric={summary.cheques} compareLabel={compareLabel} icon="uil-receipt" />
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard title="Средний чек" value={formatMetric(metricActual(summary.average_cheque))} metric={summary.average_cheque} compareLabel={compareLabel} icon="uil-chart-line" />
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard
						title="Выполнение плана"
						value={Number(data?.plan?.value || 0) > 0 ? formatPercent(data?.plan?.execution_percent) : "—"}
						compareLabel={Number(data?.plan?.value || 0) > 0
							? `план: ${shortMetric(data?.plan?.value_short, data?.plan?.value)}${data?.plan?.source === "auto" ? " (авто)" : ""}`
							: "план не задан"}
						icon="uil-focus-target"
						progress={Number(data?.plan?.value || 0) > 0 ? data?.plan?.execution_percent : null}
					/>
				</div>
			</div>

			<HealthBlock data={data} />
			<NetworkComparison data={data} />
			<BestWorstDay data={data} />
			<PeakHours trend={data?.hourly_revenue_trend} />
			<PlanHistory data={data?.plan_history} />
			<WeeklyTrend data={data?.weekly_trend} />
			<RevenueDynamics data={data?.revenue_dynamics} />
			<div className="row">
				<div className="col-xl-6">
					<ProductMovementList title="Растущие позиции" items={data?.top_products || []} type="growth" />
				</div>
				<div className="col-xl-6">
					<ProductMovementList title="Падающие позиции" items={data?.anti_rating || []} type="fall" />
				</div>
			</div>
			<DeliveryBlock data={data?.delivery_summary || {}} />
		</div>
	)
}

export default Unit
